<template>
  <div class="cci">
    <label v-if="label" class="cci__label">{{ label }}</label>
    <div class="cci__row">
      <input
          class="cci__input"
          type="text"
          :value="value"
          :placeholder="placeholder"
          :disabled="disabled"
          @input="$emit('input', $event.target.value)"
          @change="$emit('change', $event.target.value)"
          @blur="$emit('blur', $event.target.value)"
      />
      <!-- span, not label: the CRM ships global `label { height: auto !important }`
           rules (FormAddKommentar/FormSendTripToSupplier main.scss) that collapse it -->
      <span class="cci__swatch" :style="{ background: swatchColor }" :title="value">
        <input
            class="cci__picker"
            type="color"
            :value="pickerValue"
            :disabled="disabled"
            @input="onPick($event.target.value)"
        />
      </span>
    </div>
  </div>
</template>

<script>
/**
 * Color field — text input holding the raw CSS hex string exactly as stored in
 * brandings.color, plus a swatch that opens the native color picker. The picker
 * writes back '#rrggbb', the same format already in the DB, so nothing else in
 * the pipeline (client inline styles, PDF) needs to change.
 *
 * Props: value (v-model), label, placeholder, disabled
 */
export default {
  name: 'CustomColorInput',
  props: {
    value: { default: '' },
    label: { type: String, default: '' },
    placeholder: { type: String, default: '#000000' },
    disabled: { type: Boolean, default: false }
  },
  computed: {
    // <input type="color"> only accepts '#rrggbb'; expand '#rgb' and fall back
    // to black for empty/non-hex values without touching the stored text.
    pickerValue() {
      const v = String(this.value || '').trim();
      if (/^#[0-9a-fA-F]{6}$/.test(v)) return v;
      if (/^#[0-9a-fA-F]{3}$/.test(v)) {
        return '#' + v.slice(1).split('').map(c => c + c).join('');
      }
      return '#000000';
    },
    // The swatch preview renders whatever CSS understands (named colors, rgb()),
    // so show the raw value when present and grey when empty/invalid.
    swatchColor() {
      const v = String(this.value || '').trim();
      return v || '#e0e0e0';
    }
  },
  methods: {
    onPick(hex) {
      this.$emit('input', hex);
      this.$emit('change', hex);
    }
  }
};
</script>

<style scoped>
.cci { display: flex; flex-direction: column; min-width: 0; width: 100%; }
.cci__label {
  font-size: 0.78rem;
  font-weight: 600;
  color: #555;
  margin-bottom: 0.3rem;
}
.cci__row { display: flex; align-items: center; gap: 0.5rem; }
.cci__input {
  flex: 1;
  min-width: 0;
  height: 44px;
  border: 1px solid #dcdcdc;
  border-radius: 8px;
  background: #fafafa;
  padding: 0 0.85rem;
  font-size: 0.9rem;
  color: #202224;
  box-sizing: border-box;
  transition: border-color .15s ease, background .15s ease;
}
.cci__input::placeholder { color: #9aa0a6; }
.cci__input:hover { border-color: #c2c2c2; }
.cci__input:focus { outline: none; border-color: #999; background: #fff; }
.cci__input:disabled { opacity: 0.6; cursor: not-allowed; }
.cci__swatch {
  display: block;
  flex: 0 0 44px;
  width: 44px;
  min-width: 44px;
  height: 44px;
  min-height: 44px;
  border: 1px solid #dcdcdc;
  border-radius: 8px;
  cursor: pointer;
  position: relative;
  overflow: hidden;
  box-sizing: border-box;
  transition: border-color .15s ease;
}
.cci__swatch:hover { border-color: #c2c2c2; }
/* Invisible native color input stretched over the swatch so a click anywhere
   on it opens the picker while the swatch shows the current color. */
.cci__picker {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  opacity: 0;
  cursor: pointer;
  border: none;
  padding: 0;
}
.cci__picker:disabled { cursor: not-allowed; }
</style>
